import { useEffect, useMemo, useState } from 'react';
import { orderApi } from '@formosa/shared/api/order';
import { useWallet } from '../../context/WalletContext';
import {
  calcFuturesOrderPreview,
  formatOrderAmountInput,
  parseQuickOrderConfig,
  resolveQuickOrderAmount,
  validateOrderAmount,
} from '../../utils/orderValidation';
import {
  findFuturesProductByPeriod,
  formatOddsPercent,
  formatPrice,
  formatRoundOptionLabel,
  listPeriodTabsForSymbol,
} from '../../utils/futuresProduct';
import { FUTURES_PERIOD_OPTIONS } from '../../constants/futuresPeriod';

export default function FuturesOrderModal({
  open,
  direction,
  products,
  primaryProduct,
  selectedPeriodSeconds,
  currentRound,
  liveOdds,
  currentPrice,
  amount,
  submitting,
  onClose,
  onPeriodChange,
  onRoundChange,
  onAmountChange,
  onConfirm,
}) {
  const { balance, refreshWallet } = useWallet();
  const [roundOptions, setRoundOptions] = useState([]);
  const [selectedRoundCode, setSelectedRoundCode] = useState('');
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [localError, setLocalError] = useState('');

  const periodTabs = useMemo(
    () => listPeriodTabsForSymbol(products, primaryProduct?.marketSymbolCode),
    [products, primaryProduct?.marketSymbolCode]
  );

  const activeProduct = useMemo(() => {
    if (!primaryProduct?.marketSymbolCode) {
      return null;
    }
    return (
      findFuturesProductByPeriod(
        products,
        primaryProduct.marketSymbolCode,
        selectedPeriodSeconds
      ) || primaryProduct
    );
  }, [products, primaryProduct, selectedPeriodSeconds]);

  const quickOrder = useMemo(
    () => parseQuickOrderConfig(activeProduct?.playConfig),
    [activeProduct?.playConfig]
  );

  const preview = calcFuturesOrderPreview(amount, activeProduct?.handlingFeeRate);

  const selectedRound = roundOptions.find((item) => item.roundCode === selectedRoundCode);

  const validation = useMemo(
    () => validateOrderAmount(amount, activeProduct, balance, activeProduct?.handlingFeeRate),
    [amount, activeProduct, balance]
  );

  const roundOrderable = selectedRound?.orderable !== false;

  const canConfirm =
    Boolean(activeProduct?.id) &&
    Boolean(selectedRoundCode) &&
    validation.ok &&
    roundOrderable &&
    !submitting;

  useEffect(() => {
    if (!open) {
      return;
    }
    setLocalError('');
    refreshWallet();
  }, [open, refreshWallet]);

  useEffect(() => {
    if (!open || !activeProduct?.id) {
      return undefined;
    }

    let cancelled = false;
    async function loadRounds() {
      setLoadingRounds(true);
      try {
        const page = await orderApi.listAvailableFuturesRounds(activeProduct.id);
        const rows = (page?.records || []).filter((item) => item.orderable !== false);
        if (cancelled) {
          return;
        }
        setRoundOptions(rows);
        const preferredCode = currentRound?.roundCode;
        const defaultCode =
          preferredCode && rows.some((r) => r.roundCode === preferredCode)
            ? preferredCode
            : rows[0]?.roundCode || '';
        setSelectedRoundCode(defaultCode);
        if (defaultCode) {
          onRoundChange(defaultCode);
        }
      } catch {
        if (!cancelled) {
          setRoundOptions([]);
          setSelectedRoundCode('');
        }
      } finally {
        if (!cancelled) {
          setLoadingRounds(false);
        }
      }
    }

    loadRounds();
    return () => {
      cancelled = true;
    };
  }, [open, activeProduct?.id, currentRound?.roundCode, onRoundChange]);

  if (!open) {
    return null;
  }

  const isUp = direction === 'UP';
  const directionLabel = isUp ? '買漲' : '買跌';
  const balanceNum = Number(balance);
  const usableBalance = Number.isNaN(balanceNum) ? 0 : balanceNum;

  function applyPreset(preset) {
    setLocalError('');
    if (usableBalance <= 0) {
      setLocalError('USDT 餘額不足');
      return;
    }
    const resolved = resolveQuickOrderAmount(preset, usableBalance, activeProduct);
    if (resolved == null) {
      setLocalError('USDT 餘額不足');
      return;
    }
    onAmountChange(formatOrderAmountInput(resolved));
  }

  function applyRatio(ratio) {
    setLocalError('');
    if (usableBalance <= 0) {
      setLocalError('USDT 餘額不足');
      return;
    }
    const resolved = resolveQuickOrderAmount(usableBalance * ratio, usableBalance, activeProduct);
    if (resolved == null) {
      setLocalError('USDT 餘額不足');
      return;
    }
    onAmountChange(formatOrderAmountInput(resolved));
  }

  function handleRoundSelect(code) {
    setSelectedRoundCode(code);
    onRoundChange(code);
  }

  function handleConfirmClick() {
    if (!validation.ok) {
      setLocalError(validation.message);
      return;
    }
    if (!roundOrderable) {
      setLocalError('該期號已封單，請重新選擇');
      return;
    }
    setLocalError('');
    onConfirm({
      productId: activeProduct.id,
      roundCode: selectedRoundCode,
    });
  }

  return (
    <div className="trade-modal">
      <button type="button" className="trade-modal__backdrop" aria-label="關閉" onClick={onClose} />
      <div className="trade-modal__panel">
        <header className="trade-modal__header">
          <h2>交割合約</h2>
          <button type="button" className="trade-modal__close" aria-label="關閉" onClick={onClose}>
            ×
          </button>
        </header>

        <div className="trade-modal__summary">
          <div className="trade-modal__row">
            <span>投注類型</span>
            <strong className={isUp ? 'is-up' : 'is-down'}>{directionLabel}</strong>
          </div>
          <div className="trade-modal__row">
            <span>產品名稱</span>
            <strong>{activeProduct?.displayName || activeProduct?.marketSymbolCode}</strong>
          </div>
          <div className="trade-modal__row">
            <span>現價</span>
            <strong>{formatPrice(currentPrice)}</strong>
          </div>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">週期</div>
          <select
            className="trade-modal__select"
            value={selectedPeriodSeconds}
            onChange={(e) => onPeriodChange(Number(e.target.value))}
          >
            {(periodTabs.length ? periodTabs : FUTURES_PERIOD_OPTIONS).map((option) => (
              <option key={option.key || option.seconds} value={option.seconds}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">合約時間（週期ID）</div>
          <select
            className="trade-modal__select"
            value={selectedRoundCode}
            disabled={loadingRounds || roundOptions.length === 0}
            onChange={(e) => handleRoundSelect(e.target.value)}
          >
            {roundOptions.length === 0 ? (
              <option value="">暫無可下單期號</option>
            ) : (
              roundOptions.map((round) => (
                <option key={round.roundCode} value={round.roundCode}>
                  {formatRoundOptionLabel(round)}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="trade-modal__section">
          <div className="trade-modal__label">下單金額</div>
          <input
            className="trade-modal__input"
            type="number"
            min="0"
            step="0.01"
            placeholder="0"
            value={amount}
            onChange={(e) => {
              setLocalError('');
              onAmountChange(e.target.value);
            }}
          />
          <div className="trade-modal__preset-grid">
            {quickOrder.amountPresets.map((preset) => (
              <button
                key={preset}
                type="button"
                className="trade-modal__preset-btn"
                onClick={() => applyPreset(preset)}
              >
                {preset}
              </button>
            ))}
          </div>
          <div className="trade-modal__preset-grid">
            {quickOrder.amountRatios.map((ratio) => (
              <button
                key={ratio}
                type="button"
                className="trade-modal__preset-btn"
                onClick={() => applyRatio(ratio)}
              >
                {Math.round(ratio * 100)}%
              </button>
            ))}
          </div>
        </div>

        <div className="trade-modal__metrics">
          <div><span>USDT 餘額</span><strong>{balance ?? '—'}</strong></div>
          <div><span>保證金</span><strong>{formatPrice(preview.margin, 2)}</strong></div>
          <div><span>手續費</span><strong>{formatPrice(preview.fee, 2)}</strong></div>
          {selectedRound && (
            <div>
              <span>浮動賠率</span>
              <strong>
                {formatOddsPercent(
                  isUp
                    ? (liveOdds?.goLongOdds ?? selectedRound.endGoLongOdds)
                    : (liveOdds?.goShortOdds ?? selectedRound.endGoShortOdds)
                )}
              </strong>
            </div>
          )}
        </div>

        {(localError || (!validation.ok && amount)) && (
          <div className="trade-modal__error">{localError || validation.message}</div>
        )}

        <button
          type="button"
          className={`trade-modal__confirm ${canConfirm ? 'is-ready' : ''}`}
          disabled={!canConfirm}
          onClick={handleConfirmClick}
        >
          {submitting ? '下單中…' : '確認下單'}
        </button>
      </div>
    </div>
  );
}
